import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { AUTH_DT_ROUTES, DT_SESSION_CLOSED_KEY } from '../constants';
import {
  AuthContext,
  FIREBASE_UID_ST,
  getIsLocalSignOutInProgress,
  signOutDtLocal,
} from '../contexts/AuthProvider';

const isAuthRoute = pathname =>
  pathname === AUTH_DT_ROUTES.SIGN_IN || pathname === AUTH_DT_ROUTES.ENTER_PASSWORD;

const isSessionClosedStorageEvent = event => {
  if (!event.key) return false;

  if (event.key === DT_SESSION_CLOSED_KEY && event.oldValue != null && event.newValue == null) {
    return true;
  }

  if (event.key === FIREBASE_UID_ST && event.oldValue != null && event.newValue == null) {
    return true;
  }

  return false;
};

const useDtCrossTabSessionClosed = () => {
  const history = useHistory();
  const location = useLocation();
  const { user, loading } = useContext(AuthContext);
  const [showSessionClosedModal, setShowSessionClosedModal] = useState(false);
  const wasAuthenticatedRef = useRef(false);
  const hasShownSessionClosedModalRef = useRef(false);

  useEffect(() => {
    if (user) {
      wasAuthenticatedRef.current = true;
    }
  }, [user]);

  const handleRemoteSignOut = useCallback(() => {
    if (getIsLocalSignOutInProgress()) return;
    if (isAuthRoute(location.pathname)) return;
    if (!wasAuthenticatedRef.current) return;
    if (hasShownSessionClosedModalRef.current) return;

    hasShownSessionClosedModalRef.current = true;
    setShowSessionClosedModal(true);
    signOutDtLocal();
  }, [location.pathname]);

  useEffect(() => {
    const onStorage = event => {
      if (!isSessionClosedStorageEvent(event)) return;
      handleRemoteSignOut();
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [handleRemoteSignOut]);

  useEffect(() => {
    if (loading) return;
    if (user) return;
    if (!wasAuthenticatedRef.current) return;
    handleRemoteSignOut();
  }, [user, loading, handleRemoteSignOut]);

  const onAcceptSessionClosed = useCallback(() => {
    setShowSessionClosedModal(false);
    history.push(AUTH_DT_ROUTES.SIGN_IN);
  }, [history]);

  return {
    showSessionClosedModal,
    onAcceptSessionClosed,
  };
};

export default useDtCrossTabSessionClosed;
